import React, { useState, useEffect } from "react";
import MemberCard from '../MemberCard';
import { MButton } from "@/components/ui/atoms/MButtons";
import MSelectCard from "@/components/ui/atoms/MSelectCard";
import { relationshipOptions } from '@/types/relationship';
import { MInputField } from '@/components/ui/atoms/MInputField';
import { otpService } from '@/services/otp.service';
import { IconArrowNarrowRight } from '@tabler/icons-react';
import { exitFamilyAsOwner } from '@/services/members.service';
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { clearStoragePreservingTours } from '@/utils/storage-utils';

export type EligibleMember = {
  id: string;
  name: string;
  relationship: string | number;
  initials: string;
  relationshipLabel?: string;
};

interface NominateFamilyOwnerProps {
  eligibleOwnership: EligibleMember[];
  action: 'exit' | 'delete';
  loggedInMember: any;
  onSuccess?: () => void;
  onStageChange?: (stage: 'select' | 'typed' | 'otp' | 'done') => void;
}

import { useNavigate } from 'react-router-dom';

function getRelationshipLabel(relationship: string | number | undefined) {
  if (typeof relationship === 'number') {
    const rel = relationshipOptions.find(opt => opt.value === relationship);
    return rel ? rel.label : '';
  } else if (typeof relationship === 'string') {
    return relationship;
  }
  return '';
}

const NominateFamilyOwner: React.FC<NominateFamilyOwnerProps> = ({
  eligibleOwnership,
  action,
  loggedInMember,
  onSuccess,
  onStageChange,
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  
  // Helper to extract error message from nested API error response
  const getErrorMessage = (err: any, fallback: string = 'An error occurred') => {
    const apiError = err?.response?.data;
    if (typeof apiError?.message === 'string') return apiError.message;
    if (typeof apiError?.message?.message === 'string') return apiError.message.message;
    if (typeof err?.message === 'string') return err.message;
    return fallback;
  };

  const [selected, setSelected] = useState(
    eligibleOwnership.length === 1 ? eligibleOwnership[0].id : ""
  );
  // Helper to trigger OTP setup when a member is selected
  const triggerOtpForSelected = async (memberId: string) => {
    setSelected(memberId);
    // Setup OTP
    let field: 'email' | 'mobile';
    let value = '';
    let dial = '';
    if (loggedInMember?.email) {
      field = 'email';
      value = loggedInMember.email;
    } else if (loggedInMember?.mobile) {
      field = 'mobile';
      value = loggedInMember.mobile;
      dial = loggedInMember.dialCode || '';
    } else {
      showToast({ message: 'No valid contact method found.', variant: 'error' });
      return;
    }
    setOtpField(field);
    setOtpTargetValue(value);
    setDialCode(dial);
    setOtpLoading(true);
    try {
      await otpService.sendOtp({
        send_to: value,
        dialCode: field === 'mobile' ? dial : undefined,
        mode: field === 'mobile' ? 'sms' : 'email',
        type: 'member_delete',
      });
      setResendTimer(30);
      setShowOtp(true);
    } catch (err: any) {
      showToast({ message: getErrorMessage(err, 'Failed to send OTP.'), variant: 'error' });
    } finally {
      setOtpLoading(false);
    }
  };
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showTypeNameConfirm, setShowTypeNameConfirm] = useState(false);
  const [typeNameValue, setTypeNameValue] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpField, setOtpField] = useState<'email' | 'mobile'>('mobile');
  const [otpTargetValue, setOtpTargetValue] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Countdown for resendTimer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Handler for confirm button
  const handleConfirm = async () => {
    if (!selected) {
      showToast({ message: 'Please select a member', variant: 'error' });
      return;
    }
    if (action === 'exit') {
      setShowTypeNameConfirm(true);
    } else if (action === 'delete') {
      // Setup OTP
      let field: 'email' | 'mobile';
      let value = '';
      let dial = '';
      if (loggedInMember?.email) {
        field = 'email';
        value = loggedInMember.email;
      } else if (loggedInMember?.mobile) {
        field = 'mobile';
        value = loggedInMember.mobile;
        dial = loggedInMember.dialCode || '';
      } else {
        showToast({ message: 'No valid contact method found.', variant: 'error' });
        return;
      }
      setOtpField(field);
      setOtpTargetValue(value);
      setDialCode(dial);
      setOtpLoading(true);
      try {
        // Use statically imported otpService
// const { otpService } = await import('@/services/otp.service');
        await otpService.sendOtp({
          send_to: value,
          dialCode: field === 'mobile' ? dial : undefined,
          mode: field === 'mobile' ? 'sms' : 'email',
          type: 'member_delete',
        });
        setShowOtp(true);
      } catch (err: any) {
        showToast({ message: getErrorMessage(err, 'Failed to send OTP.'), variant: 'error' });
      } finally {
        setOtpLoading(false);
      }
    }
  };

  // Handler for type-name confirmation (exit)
  const handleTypeNameConfirm = async () => {
    setLoading(true);
    if (typeNameValue.trim() !== loggedInMember.firstName.toUpperCase()) {
      showToast({ message: 'Name does not match.', variant: 'error' });
      setLoading(false);
      return;
    }
    try {
      await exitFamilyAsOwner({ action: 'exit', newOwnerId: selected });
      setSuccess(true);
      navigate('/profile/family/delete-member/confirm', {
        state: {
          name: loggedInMember.firstName,
          action: 'exit',
          deletedType: 'fo-exit',
          memberId: loggedInMember.id
        }
      });
    } catch (err: any) {
      showToast({ message: getErrorMessage(err, 'Failed to exit and transfer ownership.'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handler for OTP confirmation (delete)
  const handleOtpDone = async (otp: string) => {
    setOtpLoading(true);
    onStageChange?.('otp');
    try {
      // Use statically imported otpService
// const { otpService } = await import('@/services/otp.service');
      await otpService.verifyOtp({
        send_to: otpTargetValue,
        dialCode: otpField === 'mobile' ? dialCode : undefined,
        mode: otpField === 'mobile' ? 'sms' : 'email',
        type: 'member_delete',
        otp,
      });
      await exitFamilyAsOwner({ action: 'delete', newOwnerId: selected });
      setSuccess(true);
      setShowOtp(false);
      // Remove local/session storage just in case
      clearStoragePreservingTours();
      // Show DeleteConfirm page
      // Use react-router-dom navigate
      navigate('/profile/family/delete-member/confirm', {
        state: {
          name: loggedInMember.firstName,
          action: 'delete',
          deletedType: 'fo-delete',
        }
      });
      return true;
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'OTP verification failed');
      showToast({ message: errorMsg, variant: 'error' });
      return errorMsg;
    } finally {
      setOtpLoading(false);
    }
  };

  // UI for single eligible member
  // Ensure radio is not selected by default, and OTP triggers only on select
  useEffect(() => {
    if (eligibleOwnership.length === 1) {
      setSelected(null); // clear selection on mount
    }
  }, [eligibleOwnership.length]);

  if (eligibleOwnership.length === 1 && !showTypeNameConfirm && !showOtp) {
    const member = eligibleOwnership[0];
    const relationshipLabel = member.relationshipLabel ?? getRelationshipLabel(member.relationship);
    return (
      <div style={{ float: 'left' }}>
        <MSpacer />
        <MSelectCard
          text={member.name}
          selected={selected === member.id}
          textVariant="body-base-semibold"
          onClick={() => {
            setSelected(member.id);
            if (action === 'exit') {
              onStageChange?.('typed');
              setShowTypeNameConfirm(true);
              return;
            }
            // OTP logic for delete only
            onStageChange?.('otp');
            let field: 'email' | 'mobile';
            let value = '';
            let dial = '';
            if (loggedInMember?.email) {
              field = 'email';
              value = loggedInMember.email;
            } else if (loggedInMember?.mobile) {
              field = 'mobile';
              value = loggedInMember.mobile;
              dial = loggedInMember.dialCode || '';
            } else {
              showToast({ message: 'No valid contact method found.', variant: 'error' });
              return;
            }
            setOtpField(field);
            setOtpTargetValue(value);
            setDialCode(dial);
            setOtpLoading(true);
            otpService.sendOtp({
              send_to: value,
              dialCode: field === 'mobile' ? dial : undefined,
              mode: field === 'mobile' ? 'sms' : 'email',
              type: 'member_delete',
            }).then(() => {
              setShowOtp(true);
              setResendTimer(30);
            }).catch((err: any) => {
              showToast({ message: getErrorMessage(err, 'Failed to send OTP.'), variant: 'error' });
            }).finally(() => {
              setOtpLoading(false);
            });
          }}
          aria-label={`Nominate ${member.name}`}
          className=""
        />
      </div>
    );
  }
  if (showTypeNameConfirm) {
    return (
      <div className="flex flex-col items-center justify-center mt-[34px] w-full">
        <div className="flex flex-row items-center w-full max-w-md pb-[10px]">
          <MInputField
            variant="Outlined"
            size="xl"
            placeholder={`Type ${loggedInMember.firstName.toUpperCase()}`}
            value={typeNameValue}
            onChange={e => setTypeNameValue(e.target.value)}
            className="flex-1"
          />
          <MButton
            variant="icon-only-filled"
            size="xl"
            state={loading ? 'pressed' : 'default'}
            onClick={handleTypeNameConfirm}
            disabled={typeNameValue.trim() !== (loggedInMember.firstName.toUpperCase() || '') || loading}
            icon={<IconArrowNarrowRight size={24} />}
            style={{ marginLeft: 20 }}
          />
        </div>
      </div> 
    );
  }

  // OTP confirmation UI
  if (showOtp) {
    return (
      <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto mt-12">
        {/* OTP Info Row */}
        <div className="flex flex-row items-center w-full mb-[24px]">
          <span className="text-lg">Code sent to {otpTargetValue}</span>
        </div>
        {/* OTP Input + Arrow Button Row */}
        <div className="flex flex-row items-center w-full gap-4 mb-2">
          <div className="flex-1">
            <MInputField
              size="xl"
              variant="Outlined"
              type="text"
              inputMode="numeric"
              placeholder="Enter 6-digit code"
              value={typeNameValue}
              autoFocus
              maxLength={6}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, '');
                setTypeNameValue(val);
              }}
              className="w-full"
            />
          </div>
          <MButton
            variant="icon-only-filled"
            size="xl"
            onClick={async () => {
              setOtpLoading(true);
              try {
                await handleOtpDone(typeNameValue);
              } catch (err: any) {
                showToast({ message: getErrorMessage(err, 'OTP verification failed'), variant: 'error' });
              } finally {
                setOtpLoading(false);
              }
            }}
            disabled={typeNameValue.length !== 6 || otpLoading}
            leftIcon={<IconArrowNarrowRight className="w-8 h-8" />}
            aria-label="Continue"
          />
        </div>
        <div className="w-full flex flex-col gap-1">
          <div className="flex flex-row items-center w-full justify-between mt-[20px]">
            <span className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>Didn't get a code?</span>
            <MButton
              variant="link"
              size="md"
              className="ml-auto"
              style={{ position: 'relative' }}
              disabled={resendTimer > 0 || otpLoading}
              onClick={async () => {
                setOtpLoading(true);
                try {
                  await otpService.sendOtp({
                    send_to: otpTargetValue,
                    dialCode: otpField === 'mobile' ? dialCode : undefined,
                    mode: otpField === 'mobile' ? 'sms' : 'email',
                    type: 'member_delete',
                  });
                  setResendTimer(30);
                } catch (err: any) {
                  showToast({ message: getErrorMessage(err, 'Failed to resend OTP.'), variant: 'error' });
                } finally {
                  setOtpLoading(false);
                }
              }}
            >
              Resend
            </MButton>
          </div>
          {resendTimer > 0 && (
              <div className="flex flex-row items-center w-full justify-between">
                <span style={{ fontSize: 16, fontWeight: 600, color: '#606060' }}>You can request new one in <span className="font-bold">00:{resendTimer.toString().padStart(2, '0')}</span></span>
              </div>
            )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ float: 'left' }}>
      <MSpacer mt={32}/>
      {eligibleOwnership.map((member) => {
        const relationshipLabel =
          member.relationshipLabel ?? getRelationshipLabel(member.relationship);

        return (
          <div key={member.id} className="mb-5">
            <MSelectCard
              text={member.name}
              selected={selected === member.id}
              textVariant="body-base-semibold"
              onClick={() => {
                setSelected(member.id);

                if (action === 'exit') {
                  onStageChange?.('typed');
                  setShowTypeNameConfirm(true);
                  return;
                }

                // OTP logic for delete
                onStageChange?.('otp');
                let field: 'email' | 'mobile';
                let value = '';
                let dial = '';

                if (loggedInMember?.email) {
                  field = 'email';
                  value = loggedInMember.email;
                } else if (loggedInMember?.mobile) {
                  field = 'mobile';
                  value = loggedInMember.mobile;
                  dial = loggedInMember.dialCode || '';
                } else {
                  showToast({ message: 'No valid contact method found.', variant: 'error' });
                  return;
                }

                setOtpField(field);
                setOtpTargetValue(value);
                setDialCode(dial);
                setOtpLoading(true);

                otpService
                  .sendOtp({
                    send_to: value,
                    dialCode: field === 'mobile' ? dial : undefined,
                    mode: field === 'mobile' ? 'sms' : 'email',
                    type: 'member_delete',
                  })
                  .then(() => {
                    setShowOtp(true);
                    setResendTimer(30);
                  })
                  .catch((err: any) => {
                    showToast({ message: getErrorMessage(err, 'Failed to send OTP.'), variant: 'error' });
                  })
                  .finally(() => {
                    setOtpLoading(false);
                  });
              }}
              aria-label={`Nominate ${member.name}`}
              className=""
            />
          </div>
        );
      })}
    </div>
  );
};

export default NominateFamilyOwner;
